import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import type { Dispatch, SetStateAction } from "react";
import { ColorV2 } from "config/colors";
import type { TimeReportForStamping } from "model/Company";
import { Modal } from "components/designV2/modal/modal";
import { Text } from "components/designV2/text/text";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { AutoCloseCountdown } from "components/designV2/autoCloseCountdown/autoCloseCountdown";
import {
  formatMinutesDuration,
  formatTimeLabel,
  formatTimeRangeLabel,
} from "web-apps/company/utils/timeAndDate";
import type { StampResult } from "./clockStamp.logic";
import {
  Actions,
  LoadingSpinner,
  ModalContent,
  ShiftInfo,
  SummaryLoader,
  SummaryRow,
  SummaryRows,
} from "./clockStampCheckInOutModal.styled";
import { ClockStampDeviationNotice } from "./clockStampDeviationNotice.component";
import {
  type ClockStampDeviation,
  type ClockStampSubmitData,
} from "./clockStampTimeReportPanel.logic";

interface ClockStampCheckInOutModalProps {
  readonly hasTimeReport: boolean;
  readonly isAutoGeneratedGig: boolean;
  readonly isSubmittingTimeReport: boolean;
  readonly isTimeReportLoading: boolean;
  readonly showModal: boolean;
  readonly stampResult: StampResult | null;
  readonly timeReport?: TimeReportForStamping;
  readonly timeReportDefaultSubmitData: ClockStampSubmitData | null;
  readonly timeReportDeviations: readonly ClockStampDeviation[];
  readonly clearInputs: () => void;
  readonly handleEditTimeReport: () => void;
  readonly handleSubmitDefaultTimeReport: () => void;
  readonly setIsAutoGeneratedGig: Dispatch<SetStateAction<boolean>>;
  readonly setKeyShouldBeDisabled: Dispatch<SetStateAction<boolean>>;
  readonly setShowModal: Dispatch<SetStateAction<boolean>>;
}

export const ClockStampCheckInOutModal = (
  props: ClockStampCheckInOutModalProps,
): JSX.Element => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const isCheckOut = Boolean(
    props.timeReport?.checkInTime &&
    !props.isAutoGeneratedGig &&
    !props.timeReport.checkOutTime,
  );
  const userName = (props.timeReport?.firstName || "").trim();
  const greeting = isCheckOut
    ? t("StampClock.HaveANiceDay")
    : t("StampClock.Welcome");
  const greetingText = userName ? `${greeting} ${userName}!` : `${greeting}!`;

  const checkInTime =
    props.stampResult?.checkInTime ?? props.timeReport?.checkInTime;

  const breakMinutes =
    Number(props.timeReportDefaultSubmitData?.breakTime) || 0;
  const breakLabel =
    breakMinutes > 0 &&
    props.timeReportDefaultSubmitData?.breakStartTime &&
    props.timeReportDefaultSubmitData?.breakEndTime
      ? t("TimeReports.BreakWithRange", {
          duration: formatMinutesDuration(breakMinutes),
          range: formatTimeRangeLabel(
            props.timeReportDefaultSubmitData.breakStartTime,
            props.timeReportDefaultSubmitData.breakEndTime,
            locale,
          ),
        })
      : formatMinutesDuration(breakMinutes);
  const isBusy = props.isTimeReportLoading || props.isSubmittingTimeReport;
  // Editing stays available when the defaults cannot be submitted as they are,
  // since that is the case where the report has to be adjusted by hand.
  const isEditDisabled = isBusy || !props.hasTimeReport;
  const isSubmitDisabled = isBusy || !props.timeReportDefaultSubmitData;
  const hasTimeDeviations = props.timeReportDeviations.length > 0;
  const clearInputs = props.clearInputs;
  const setIsAutoGeneratedGig = props.setIsAutoGeneratedGig;
  const setKeyShouldBeDisabled = props.setKeyShouldBeDisabled;
  const setShowModal = props.setShowModal;

  const handleCloseModal = useCallback((): void => {
    setShowModal(false);
    setKeyShouldBeDisabled(false);
    setIsAutoGeneratedGig(false);
    clearInputs();
  }, [
    clearInputs,
    setIsAutoGeneratedGig,
    setKeyShouldBeDisabled,
    setShowModal,
  ]);

  return (
    <Modal
      label=""
      size="compact"
      isOpen={props.showModal}
      onClose={handleCloseModal}
    >
      <ModalContent>
        <Text typography="h3" align="center" color={ColorV2.Blue1}>
          {greetingText}
        </Text>
        {isCheckOut ? (
          <>
            {props.isTimeReportLoading ? (
              <SummaryLoader>
                <LoadingSpinner
                  aria-label={t("General.Loading")}
                  role="status"
                />
              </SummaryLoader>
            ) : (
              props.timeReportDefaultSubmitData && (
                <SummaryRows>
                  <SummaryRow>
                    <Text typography="b2" color={ColorV2.Blue1}>
                      {t("StampClock.WorkingHours")}
                    </Text>
                    <Text typography="b2" color={ColorV2.Blue1}>
                      {formatTimeRangeLabel(
                        props.timeReportDefaultSubmitData.startTime,
                        props.timeReportDefaultSubmitData.endTime,
                        locale,
                      )}
                    </Text>
                  </SummaryRow>
                  <SummaryRow>
                    <Text typography="b2" color={ColorV2.Blue1}>
                      {t("TimeReports.Break")}
                    </Text>
                    <Text typography="b2" color={ColorV2.Blue1}>
                      {breakLabel}
                    </Text>
                  </SummaryRow>
                </SummaryRows>
              )
            )}
            {hasTimeDeviations && (
              <ClockStampDeviationNotice
                deviations={props.timeReportDeviations}
              />
            )}
            {!hasTimeDeviations && (
              <Text typography="b1" align="center" color={ColorV2.Blue1}>
                {t("StampClock.SubmitTimeReportPrompt")}
              </Text>
            )}
            <Actions>
              <SecondaryButton
                label={t(
                  hasTimeDeviations
                    ? "StampClock.AddComment"
                    : "StampClock.Comment",
                )}
                onClick={props.handleEditTimeReport}
                disabled={isEditDisabled}
                size="small"
              />
              <PrimaryButton
                label={t("StampClock.Submit")}
                onClick={props.handleSubmitDefaultTimeReport}
                disabled={isSubmitDisabled}
                isLoading={props.isSubmittingTimeReport}
                size="small"
              />
            </Actions>
          </>
        ) : (
          <>
            <Text typography="b1" align="center" color={ColorV2.Blue1}>
              {t("StampClock.YouAreCheckedInAt", {
                time: formatTimeLabel(checkInTime, locale),
              })}
            </Text>
            {props.timeReport && (
              <ShiftInfo>
                <Text typography="b2" align="center" color={ColorV2.Blue1}>
                  {t("StampClock.ShiftToday")}
                </Text>
                <Text typography="b2" align="center" color={ColorV2.Blue1}>
                  {formatTimeRangeLabel(
                    props.timeReport.startTime,
                    props.timeReport.endTime,
                    locale,
                  )}
                </Text>
              </ShiftInfo>
            )}
            <AutoCloseCountdown onComplete={handleCloseModal} />
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
